import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeatureContext';
import { AppLayout } from '../../components/layout/AppLayout';
import { GlobalSearch } from '../../components/GlobalSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { HOMEPAGE_SHORTCUTS } from '../../utils/featureRouteMapping';
import { 
  Wallet, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Send, 
  TrendingUp,
  Bell,
  Search
} from 'lucide-react';

export const HomePage = () => {
  const { user } = useAuth();
  const { isFeatureEnabled } = useFeatures();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/portfolio`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Portfolio fetched successfully:', data);
        setPortfolio(data);
      } else {
        const errorText = await response.text();
        console.error('❌ Portfolio fetch failed:', response.status, errorText);
        setPortfolio(null);
      }
    } catch (error) {
      console.error('❌ Failed to fetch portfolio:', error.message);
      setPortfolio(null);
    } finally {
      setLoading(false);
    }
  };

  const totalBalance = portfolio?.total_portfolio_value || 0;
  const spendableBalance = portfolio?.spendable_balance || 0;

  const quickActions = [
    { label: 'Deposit', icon: ArrowDownLeft, path: '/user/deposit', color: 'bg-green-500', feature: 'deposits' },
    { label: 'Withdraw', icon: ArrowUpRight, path: '/user/withdraw', color: 'bg-red-500', feature: 'withdrawals' },
    { label: 'Transfer', icon: Send, path: '/user/transfer', color: 'bg-blue-500', feature: 'transfers' },
    { label: 'Swap', icon: TrendingUp, path: '/user/swap', color: 'bg-purple-500', feature: 'swap' },
  ];

  const navigateToFeature = (path) => {
    navigate(path, { state: { fromSuperApp: true } });
  };

  return (
    <>
      {showSearch && <GlobalSearch onClose={() => setShowSearch(false)} />}
      
      <AppLayout>
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Welcome, {user?.name || 'User'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Matrise
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/user/notifications')}
              className="relative"
            >
              <Bell className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSearch(true)}
              data-testid="open-search"
            >
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Portfolio Summary */}
        <Card className="bg-gradient-to-br from-blue-600 to-purple-600 text-white border-0">
          <CardContent className="pt-6">
            <div className="mb-4">
              <p className="text-sm opacity-90 mb-1">Total Portfolio Value</p>
              <h2 className="text-3xl font-bold">
                {loading ? '...' : `R${totalBalance.toFixed(2)}`}
              </h2>
            </div>
            
            <div className="mb-4 p-3 bg-white/10 rounded-lg">
              <p className="text-xs opacity-90 mb-1">Spendable Balance</p>
              <p className="text-xl font-semibold">
                {loading ? '...' : `R${spendableBalance.toFixed(2)}`}
              </p>
              <p className="text-xs opacity-75 mt-1">Available to spend now</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => navigate('/user/wallets')}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Wallet className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Quick Actions</h3>
          <div className="grid grid-cols-4 gap-3">
            {quickActions
              .filter(action => !action.feature || isFeatureEnabled(action.feature))
              .map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.label}
                  onClick={() => navigateToFeature(action.path)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-md transition-all"
                >
                  <div className={`${action.color} p-3 rounded-full`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {action.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Shortcuts */}
        {HOMEPAGE_SHORTCUTS.filter(shortcut => isFeatureEnabled(shortcut.feature)).length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Shortcuts</h3>
            <div className="grid grid-cols-2 gap-3">
              {HOMEPAGE_SHORTCUTS
                .filter(shortcut => isFeatureEnabled(shortcut.feature))
                .map((shortcut) => (
                  <Card 
                    key={shortcut.path}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(shortcut.path)}
                  >
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{shortcut.label}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{shortcut.description}</p>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
    </>
  );
};
